import { createLatestRequestGuard } from "./latestRequestGuard";

export interface SerialLatestObserver<TResult> {
  onLatestSuccess: (result: TResult) => void;
  onLatestError: () => void | Promise<void>;
  onLatestSettled: () => void;
}

export interface SerialLatestExecutor<TInput, TResult> {
  execute: (
    input: TInput,
    task: (value: TInput) => Promise<TResult>,
    observer: SerialLatestObserver<TResult>
  ) => void;
  invalidate: () => void;
  whenIdle: () => Promise<void>;
}

export function createSerialLatestExecutor<
  TInput,
  TResult
>(): SerialLatestExecutor<TInput, TResult> {
  const requestGuard = createLatestRequestGuard();
  let running = false;
  let idleResolvers: Array<() => void> = [];
  let pending:
    | {
        input: TInput;
        task: (value: TInput) => Promise<TResult>;
        observer: SerialLatestObserver<TResult>;
        request: number;
      }
    | undefined;

  const run = async (operation: NonNullable<typeof pending>): Promise<void> => {
    running = true;
    try {
      const result = await operation.task(operation.input);
      if (requestGuard.isCurrent(operation.request)) {
        operation.observer.onLatestSuccess(result);
      }
    } catch {
      if (requestGuard.isCurrent(operation.request)) {
        await operation.observer.onLatestError();
      }
    } finally {
      if (requestGuard.isCurrent(operation.request)) {
        operation.observer.onLatestSettled();
      }
      const next = pending;
      pending = undefined;
      if (next) await run(next);
      else {
        running = false;
        const resolvers = idleResolvers;
        idleResolvers = [];
        resolvers.forEach((resolve) => resolve());
      }
    }
  };

  const execute = (
    input: TInput,
    task: (value: TInput) => Promise<TResult>,
    observer: SerialLatestObserver<TResult>
  ) => {
    const operation = { input, task, observer, request: requestGuard.begin() };
    if (running) pending = operation;
    else void run(operation);
  };

  const invalidate = () => {
    requestGuard.invalidate();
    pending = undefined;
  };

  const whenIdle = () => {
    if (!running) return Promise.resolve();
    return new Promise<void>((resolve) => idleResolvers.push(resolve));
  };

  return { execute, invalidate, whenIdle };
}
